import { z } from "zod";
import { PROGRAMME_ATTENDANCE_STATUSES, PROGRAMME_MEETING_STATUSES, } from "../constants/programme-body-states.js";
// 'draft' | 'approved' — v1 minutes are one row per meeting (not versioned);
// see the class comment on programmeBodyMeetingMinutes in
// packages/db/src/schema/programme-bodies.ts. Not promoted to a shared
// constant (unlike PROGRAMME_MEETING_STATUSES) because nothing outside the
// minutes slice needs this vocabulary yet.
export const PROGRAMME_MEETING_MINUTES_STATUSES = ["draft", "approved"];
// 'open' | 'completed'.
export const PROGRAMME_MEETING_ACTION_STATUSES = ["open", "completed"];
export const createProgrammeBodyMeetingSchema = z.object({
    title: z.string().max(200).optional(),
    startsAt: z.string().datetime(),
    location: z.string().max(200).optional(),
});
// Carry-forward ceremony chain: schedule the next meeting off an existing
// one, copying its still-open actions (see `createNextMeetingWithCarryForward`
// in `programme-body-meeting.service.ts`). Same shape as
// `createProgrammeBodyMeetingSchema` plus the required link back to the
// meeting being closed out.
export const createProgrammeBodyNextMeetingSchema = createProgrammeBodyMeetingSchema.extend({
    previousMeetingId: z.string().uuid(),
});
export const transitionProgrammeBodyMeetingSchema = z.object({
    status: z.enum(PROGRAMME_MEETING_STATUSES),
});
export const setProgrammeBodyMeetingAttendanceSchema = z.object({
    status: z.enum(PROGRAMME_ATTENDANCE_STATUSES),
});
export const createProgrammeBodyMeetingAgendaItemSchema = z.object({
    title: z.string().min(1).max(200),
    notes: z.string().max(4000).optional(),
    sortOrder: z.number().int().min(0).optional(),
    paperDocumentId: z.string().uuid().nullable().optional(),
});
export const updateProgrammeBodyMeetingAgendaItemSchema = createProgrammeBodyMeetingAgendaItemSchema.partial();
export const reorderProgrammeBodyMeetingAgendaSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
export const saveProgrammeBodyMeetingMinutesSchema = z.object({
    body: z.string().max(50000),
});
export const createProgrammeBodyMeetingActionSchema = z.object({
    contactId: z.string().uuid().optional(),
    description: z.string().min(1).max(2000),
    dueAt: z.string().datetime().optional(),
});
export const programmeBodyMeetingSchema = z.object({
    id: z.string().uuid(),
    bodyId: z.string().uuid(),
    title: z.string().max(200).nullable().default(null),
    startsAt: z.string().datetime(),
    location: z.string().max(200).nullable().default(null),
    status: z.enum(PROGRAMME_MEETING_STATUSES),
    previousMeetingId: z.string().uuid().nullable().default(null),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const programmeBodyMeetingAttendanceSchema = z.object({
    id: z.string().uuid(),
    meetingId: z.string().uuid(),
    contactId: z.string().uuid(),
    status: z.enum(PROGRAMME_ATTENDANCE_STATUSES),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const programmeBodyMeetingAgendaItemSchema = z.object({
    id: z.string().uuid(),
    meetingId: z.string().uuid(),
    title: z.string().max(200),
    notes: z.string().max(4000).nullable().default(null),
    paperDocumentId: z.string().uuid().nullable().default(null),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const programmeBodyMeetingMinutesSchema = z.object({
    id: z.string().uuid(),
    meetingId: z.string().uuid(),
    status: z.enum(PROGRAMME_MEETING_MINUTES_STATUSES),
    body: z.string().max(50000),
    approvedAt: z.string().datetime().nullable().default(null),
    approvedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const programmeBodyMeetingActionSchema = z.object({
    id: z.string().uuid(),
    meetingId: z.string().uuid(),
    contactId: z.string().uuid().nullable().default(null),
    description: z.string().max(2000),
    dueAt: z.string().datetime().nullable().default(null),
    status: z.enum(PROGRAMME_MEETING_ACTION_STATUSES),
    completedAt: z.string().datetime().nullable().default(null),
    completedBy: z.string().email().nullable().default(null),
    carriedFromActionId: z.string().uuid().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// List-response envelopes — every apiFetch call validates against a shared
// schema (04-shared/ssot.md R6), same convention as
// `programmeBodyListResponseSchema` in ./programme-bodies.js.
export const programmeBodyMeetingListResponseSchema = z.object({
    meetings: z.array(programmeBodyMeetingSchema),
});
export const programmeBodyMeetingAttendanceListResponseSchema = z.object({
    attendance: z.array(programmeBodyMeetingAttendanceSchema),
});
export const programmeBodyMeetingAgendaItemListResponseSchema = z.object({
    agendaItems: z.array(programmeBodyMeetingAgendaItemSchema),
});
export const programmeBodyMeetingActionListResponseSchema = z.object({
    actions: z.array(programmeBodyMeetingActionSchema),
});
//# sourceMappingURL=programme-body-meetings.js.map